import React, { useEffect, useMemo, useRef, useState } from 'react';
import { previewAnalysisCost, submitInitialRun, fetchInitialRunCreditBalance } from '../../../services/batchService';
import {
  getRetainedExecutionRequestSubmission, retainExecutionRequestSubmission,
  clearRetainedExecutionRequestSubmission, readAcceptedInitialRuns, recordAcceptedInitialRun,
} from '../../../utils/executionRequestIdentity';
import { useExecutionRequestPolling } from '../hooks/useExecutionRequestPolling';
import { ActiveExecutionRequestPanel } from './ActiveExecutionRequestPanel';

const PIPELINES = ['size', 'encapsulation'];
const isPairingConflict = (error) => error?.response?.status === 409 &&
  ['PAIRING_REQUIRED', 'PAIRING_INVALID'].includes(error?.response?.data?.code);
const isPreparationConflict = (error) => error?.response?.status === 409 && [
  'stale_cycle', 'stale_revision', 'stale_preparation', 'closed_cycle',
  'legacy_execution', 'execution_evidence', 'inactive_batch', 'preparation_not_enabled',
  'unclassified_measurement', 'noncurrent_measurement',
].includes(error?.response?.data?.code);
const scopeFor = (batchId, pipeline) => ({ batchId, pipeline, requestType: 'batch-run' });
const messageFor = (error) => {
  const data = error?.response?.data;
  return (typeof data?.detail === 'string' ? data.detail : null) ||
    (data ? JSON.stringify(data) : error?.message) || 'Request failed.';
};

function InitialExecution({ entry, runs, onOpenRun, onRefreshRuns, getSampleLabel, getInputLabel }) {
  const id = entry.response.execution_request_id;
  const { executionRequest, pollingError } = useExecutionRequestPolling(id);
  const current = String(executionRequest?.execution_request_id) === String(id) ? executionRequest : entry.response;
  const lastRefresh = useRef(null);
  const [refreshError, setRefreshError] = useState(false);
  const signature = JSON.stringify([current.status, (current.plan_items || []).map((item) => [item.status, item.latest_attempt?.status, item.workflow_run_id])]);
  useEffect(() => {
    if (lastRefresh.current === signature) return;
    lastRefresh.current = signature;
    Promise.resolve(onRefreshRuns()).then(() => setRefreshError(false)).catch(() => { lastRefresh.current = null; setRefreshError(true); });
  }, [signature, onRefreshRuns]);
  return <section aria-label={`${entry.pipeline} cycle ${entry.cycle} execution`} className="rounded border p-3">
    <p>{entry.pipeline} · Closed cycle {entry.cycle} · Execution {id} · {current.status || 'pending'}</p>
    <p>{entry.replayed ? 'Existing execution recovered; no new charge.' : 'Initial New Run accepted.'}</p>
    {entry.replayed && <p>Replay recovers request identity; it does not restart execution or repair task publication.</p>}
    {entry.publicationFailed && <p role="alert">Accepted, but task publication failed. The cycle remains closed. Request replay does not repair publication; operational recovery is required.</p>}
    {pollingError && <p role="status">Live status unavailable. Polling will retry automatically.</p>}
    {refreshError && <p role="status">Saved results could not be refreshed. <button onClick={() => Promise.resolve(onRefreshRuns()).then(() => setRefreshError(false)).catch(() => setRefreshError(true))}>Refresh saved results</button></p>}
    <ActiveExecutionRequestPanel getSampleLabel={getSampleLabel} getInputLabel={getInputLabel} executionRequest={current} representedWorkflowRunIds={runs.map((run) => run.id)} onOpenRun={onOpenRun} />
  </section>;
}

// Mounted once per Batch. Requests and trackers remain alive when tabs change.
export function InitialRunPreparation({ view = 'all', onNavigate, batchId, pipeline, state, measurements, blocked,
  createClientRequestId, onRefresh, onConflict, runs, onRefreshRuns, onOpenRun, organizationId, getSampleLabel, getInputLabel }) {
  const [retained, setRetained] = useState(() => Object.fromEntries(PIPELINES.map((p) =>
    [p, getRetainedExecutionRequestSubmission(scopeFor(batchId, p))])));
  const [accepted, setAccepted] = useState(() => readAcceptedInitialRuns(batchId));
  const [messages, setMessages] = useState({});
  const [busy, setBusy] = useState({});
  const inFlight = useRef(new Set());
  const [pairsByScope, setPairsByScope] = useState({});
  const [pairChoices, setPairChoices] = useState({});
  const [pairingError, setPairingError] = useState(null);
  const pairingControls = useRef(null);
  const pairingSummary = useRef(null);
  const [quoteResult, setQuoteResult] = useState(null);
  const [balance, setBalance] = useState(null);
  const [balanceError, setBalanceError] = useState(false);
  const balanceRequest = useRef(0);
  const [quoteRefresh, setQuoteRefresh] = useState(0);
  const conflictScopes = useRef(new Set());
  const scopeKey = JSON.stringify([pipeline, state?.current_cycle_number, state?.preparation_revision]);
  const pairs = pairsByScope[scopeKey] || [];
  const pairsJson = JSON.stringify(pairs);
  const payload = useMemo(() => ({ pipelines: [pipeline],
    cycle_number: state?.current_cycle_number, preparation_revision: state?.preparation_revision,
    ...(pipeline === 'encapsulation' && pairsJson !== '[]' ? { encapsulation: { pairs: JSON.parse(pairsJson) } } : {}),
  }), [pipeline, state?.current_cycle_number, state?.preparation_revision, pairsJson]);
  const quoteKey = JSON.stringify(payload);
  const pairingProblem = pairingError?.key === quoteKey ? pairingError.data : null;
  useEffect(() => {
    if (pipeline !== 'encapsulation' || !pairingProblem || !pairingControls.current) return;
    onNavigate?.('configure');
    pairingControls.current.open = true;
    pairingSummary.current?.focus();
  }, [pipeline, pairingProblem, onNavigate]);
  const eligible = measurements.filter((m) => m.pipeline === pipeline && m.cycle_number === state?.current_cycle_number && m.status === 'accepted' && !m.is_excluded);
  const alreadyAccepted = accepted.some((entry) => entry.pipeline === pipeline && entry.cycle === state?.current_cycle_number);
  const canPrepare = !blocked && !alreadyAccepted && Number.isInteger(state?.current_cycle_number) && Number.isInteger(state?.preparation_revision);
  const pending = retained[pipeline];
  const quote = quoteResult?.key === quoteKey ? quoteResult : null;

  useEffect(() => {
    let active = true;
    setQuoteResult(null);
    if (!canPrepare || pending || !eligible.length) return () => { active = false; };
    Promise.resolve().then(() => previewAnalysisCost(batchId, payload)).then((data) => {
      if (active) setQuoteResult({ key: quoteKey, data });
    }).catch((error) => {
      if (!active) return;
      setQuoteResult({ key: quoteKey, error: messageFor(error), diagnostics: error?.response?.data });
      if (isPairingConflict(error)) {
        setPairingError({ key: quoteKey, data: error.response.data });
      } else if (isPreparationConflict(error) && !conflictScopes.current.has(quoteKey)) {
        conflictScopes.current.add(quoteKey);
        void onConflict(error);
      }
    });
    return () => { active = false; };
  }, [batchId, canPrepare, pending, eligible.length, payload, quoteKey, quoteRefresh, onConflict]);

  const notify = (target, text) => setMessages((previous) => ({ ...previous, [target]: text }));
  const clearPending = (target) => {
    clearRetainedExecutionRequestSubmission(scopeFor(batchId, target));
    setRetained((previous) => ({ ...previous, [target]: null }));
  };
  const accept = async (response, original, status) => {
    const target = original.pipelines[0];
    const prior = readAcceptedInitialRuns(batchId).find((item) => item.response.execution_request_id === response.execution_request_id);
    const entry = { pipeline: target, cycle: original.cycle_number, response,
      replayed: status === 200, publicationFailed: status === 503 || Boolean(prior?.publicationFailed) };
    try {
      setAccepted(recordAcceptedInitialRun(batchId, entry));
      clearPending(target);
    } catch (_) {
      setAccepted((previous) => [...previous.filter((item) => item.response.execution_request_id !== response.execution_request_id), entry]);
      notify(target, 'Execution accepted, but storing its identity failed. Keep the retained request for exact-payload recovery.');
    }
    // Acceptance is durable even if these read-only refreshes fail.
    try { await onRefresh(); }
    catch (_) { notify(target, 'Execution accepted. Refresh failed; reload before preparing another cycle.'); }
    onNavigate?.('runs');
    setQuoteRefresh((value) => value + 1);
    const organization = organizationId || quote?.data?.organization_id;
    if (organization) {
      const request = ++balanceRequest.current;
      setBalance(null);
      try {
        const fresh = await fetchInitialRunCreditBalance(organization);
        if (request === balanceRequest.current) { setBalance(fresh.balance); setBalanceError(false); }
      } catch (_) { if (request === balanceRequest.current) setBalanceError(true); }
    }
  };
  const submit = async (recover = false) => {
    const target = pipeline;
    if (inFlight.current.has(target)) return;
    if (!recover && (!canPrepare || pending || !eligible.length)) return;
    inFlight.current.add(target);
    setBusy((previous) => ({ ...previous, [target]: true }));
    notify(target, '');
    let original;
    let recovering = recover;
    try {
      const existing = getRetainedExecutionRequestSubmission(scopeFor(batchId, target));
      recovering = recover || Boolean(existing);
      if (existing?.legacyScope) throw new Error('A legacy Batch-wide request requires reconciliation before another initial admission.');
      if (recover && !existing) throw new Error('No retained request is available. Reload the workspace.');
      const submission = existing || retainExecutionRequestSubmission({
        ...scopeFor(batchId, target), payload, createClientRequestId,
      });
      original = submission.payload;
      setRetained((previous) => ({ ...previous, [target]: submission }));
      const response = await submitInitialRun(batchId, original);
      if (![200, 202].includes(response?.status) || !response?.data?.execution_request_id) {
        throw new Error('The response did not establish acceptance. Recover using the original request.');
      }
      await accept(response.data, original, response.status);
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      if (status === 503 && data?.execution_request_id && original) {
        await accept(data, original, status);
      } else {
        // A recovery conflict can indicate an existing key with different intent.
        // Keep it unresolved rather than offer a new UUID that could charge again.
        const rejected = status === 402 || (!recovering && [400, 403, 404, 409, 422].includes(status));
        if (original && rejected) clearPending(target);
        notify(target, `${messageFor(error)}${original && !rejected ? ' Outcome unresolved. Recover the original request; do not submit another New Run.' : ''}`);
        if (isPairingConflict(error)) {
          setPairingError({ key: quoteKey, data });
        } else if (isPreparationConflict(error)) {
          await onConflict(error);
        }
        if (status === 402) setQuoteRefresh((value) => value + 1);
      }
    } finally {
      inFlight.current.delete(target);
      setBusy((previous) => ({ ...previous, [target]: false }));
    }
  };

  // Offer every current eligible input, not just ambiguous Samples returned by
  // pairing diagnostics. Explicit pairs must cover the whole cycle.
  const groups = pipeline === 'encapsulation' ? [...new Set(eligible.map((m) => m.sample_id).filter((id) => id != null))] : [];
  const addPair = (sampleId) => {
    const choice = pairChoices[`${scopeKey}:${sampleId}`];
    if (!choice?.non_lysed_id || !choice?.lysed_id) return;
    const pair = { sample_id: Number(sampleId), non_lysed_id: Number(choice.non_lysed_id), lysed_id: Number(choice.lysed_id) };
    setPairsByScope((previous) => ({ ...previous, [scopeKey]: [...pairs, pair] }));
  };
  return <section hidden={!['all', 'configure', 'review', 'run', 'runs'].includes(view) || (view === 'configure' && pipeline === 'size')} aria-label="Initial New Run" className="space-y-3 rounded border p-3">
    <div hidden={!['all', 'review', 'run'].includes(view)}>
    <p>New Run covers all accepted, non-excluded Measurements in this pipeline cycle. Saved Size trace selections are authoritative. Credit estimates do not establish scientific readiness.</p>
    {pipeline === 'encapsulation' && <p>{pairs.length ? `Explicit pairs: ${pairs.map((pair) => `${pair.non_lysed_id} + ${pair.lysed_id}`).join('; ')}` : 'Automatic pairing will be validated; use Configure if explicit pairing is required.'}</p>}
    {balance != null && <p>Updated credit balance: {balance}</p>}
    {balanceError && <p role="status">Execution accepted; credit balance refresh unavailable. Check your credit profile.</p>}
    {quote?.data && <p>Estimated credits: {quote.data.total_cost} · Balance: {quote.data.balance}{quote.data.sufficient_credits === false ? ' · Insufficient credits' : ''}</p>}
    {quote?.error && <p role="status">Credit preview: {quote.error}</p>}
    {canPrepare && eligible.length > 0 && !pending && !quote && <p role="status">Loading credit preview…</p>}
    </div>
    <div hidden={!['all', 'configure'].includes(view)}>
    {pipeline === 'encapsulation' && groups.length > 0 && <details ref={pairingControls} aria-label="Explicit Encapsulation pairing">
      <summary className="cursor-pointer rounded bg-blue-50 p-3 font-semibold text-blue-800" ref={pairingSummary} tabIndex={-1}>Explicit Encapsulation pairing</summary>
      {pairingProblem && <div role="alert">
        <p>{pairingProblem.code === 'PAIRING_REQUIRED'
          ? 'Choose and add explicit pairs for the Samples below, covering every eligible Measurement.'
          : 'Review the indicated pairs, remove incorrect pairs and add corrected pairs before submitting again.'}</p>
        {Array.isArray(pairingProblem.issues) && <ul>{pairingProblem.issues.map((issue, index) =>
          <li key={index}>Pair {Number.isInteger(issue.pair_index) ? issue.pair_index + 1 : '?'}{issue.sample_id != null ? ` · Sample ${issue.sample_id}` : ''}: {issue.detail || issue.code}</li>
        )}</ul>}
      </div>}
      <p>When explicit pairs are supplied, include every eligible Measurement. Inputs may be reused across valid pairs. The backend validates coverage.</p>
      {groups.map((sampleId) => <fieldset key={sampleId} disabled={Boolean(pending) || busy[pipeline] || !canPrepare}>
        <legend>Sample {sampleId}</legend>
        {['non_lysed_id', 'lysed_id'].map((role) => <label key={role}>{role === 'non_lysed_id' ? 'Non-lysed' : 'Lysed'} for Sample {sampleId}
          <select className="mx-2 rounded border border-slate-300 p-2" value={pairChoices[`${scopeKey}:${sampleId}`]?.[role] || ''} onChange={(event) => {
            const value = event.target.value;
            setPairChoices((previous) => ({ ...previous, [`${scopeKey}:${sampleId}`]: { ...previous[`${scopeKey}:${sampleId}`], [role]: value } }));
          }}><option value="">Choose Measurement</option>{eligible.filter((m) => m.sample_id === sampleId).map((m) => <option key={m.id} value={m.id}>{m.filename} ({m.file_role || 'raw'})</option>)}</select>
        </label>)}
        <button className="rounded border border-blue-600 px-3 py-2 font-semibold text-blue-700 hover:bg-blue-50" onClick={() => addPair(sampleId)}>Add pair for Sample {sampleId}</button>
      </fieldset>)}
      {pairs.map((pair, index) => <p key={index}>Sample {pair.sample_id}: {pair.non_lysed_id} + {pair.lysed_id} <button disabled={Boolean(pending) || busy[pipeline] || !canPrepare} onClick={() => setPairsByScope((previous) => ({ ...previous, [scopeKey]: pairs.filter((_, i) => i !== index) }))}>Remove pair {index + 1}</button></p>)}
    </details>}
    {pipeline === 'encapsulation' && !groups.length && <p>Assign accepted Measurements to Samples before configuring pairing.</p>}
    </div>
    <div hidden={!['all', 'run'].includes(view)}>
    <h2 className="text-xl font-semibold">Run {pipeline === 'size' ? 'Size' : 'Encapsulation'}</h2>
    {!canPrepare && <p role="status">New Run unavailable: preparation is protected or current cycle state is unavailable.</p>}
    {canPrepare && !eligible.length && <p role="status">Upload accepted, included Measurements before starting a New Run.</p>}
    {busy[pipeline] && <p role="status">Submitting request…</p>}
    {canPrepare && eligible.length > 0 && !pending && !busy[pipeline] && <p>Ready to submit for backend validation.</p>}
    {messages[pipeline] && <p role="alert">{messages[pipeline]}</p>}
    {pending ? <div role="status">
      <p>Unresolved {pipeline} request for cycle {pending.payload.cycle_number}. Recovery resends the exact original payload and UUID, even if preparation has advanced.</p>
      {pending.legacyScope ? <p role="alert">A legacy Batch-wide submission is retained. Reconcile its outcome before starting a pipeline cycle.</p> : <button disabled={busy[pipeline]} onClick={() => submit(true)}>Recover original request</button>}
    </div> : <button className="rounded bg-blue-700 px-5 py-3 font-semibold text-white hover:bg-blue-800 disabled:bg-slate-300 disabled:text-slate-600" disabled={!canPrepare || !eligible.length || busy[pipeline] || quote?.data?.sufficient_credits === false} onClick={() => submit(false)}>New Run — {pipeline === 'size' ? 'Size' : 'Encapsulation'}</button>}
    {PIPELINES.filter((p) => p !== pipeline && retained[p]).map((p) => <p key={p} role="status">Unresolved {p} request retained. Switch to its preparation tab to recover it.</p>)}
    </div>
    <div hidden={!['all', 'runs'].includes(view)}>
    {accepted.map((entry) => <InitialExecution key={entry.response.execution_request_id} entry={entry} runs={runs} onOpenRun={onOpenRun} onRefreshRuns={onRefreshRuns} getSampleLabel={getSampleLabel} getInputLabel={getInputLabel} />)}
    </div>
  </section>;
}
